import { type Dispatch, type SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLatestRef } from 'hooks/useLatestRef';
import type { IssueWorkflowOutDto } from 'shared/domain/workflow/types';
import { findById } from 'shared/utils/id/findById';
import { findStagesByWorkflowId } from 'store/workflow/model';
import {
  buildOriginalSnapshot,
  buildWorkflowContentSignature,
  computeSaveDiff,
  hasWorkflowChanges,
  serverStageToRow,
} from './model';
import type { WorkflowOriginalSnapshot, WorkflowStageRow } from './types';

export interface WorkflowDraft {
  /** The edited workflow as the server has it; `null` until it is loaded. */
  workflow: IssueWorkflowOutDto | null;
  workflowName: string;
  setWorkflowName: (name: string) => void;
  localStages: WorkflowStageRow[];
  setLocalStages: Dispatch<SetStateAction<WorkflowStageRow[]>>;
  /** What the server had when the draft started: a save sends the difference. */
  getOriginalSnapshot: () => WorkflowOriginalSnapshot | null;
  /** Starts the draft again from the server's workflow (after a save). */
  resetToServer: () => void;
}

/**
 * The editor's draft of a workflow, kept until it is saved. It starts from the
 * server's workflow and follows it only while untouched: the list reloads on
 * every return to the tab, every flush and every change elsewhere, and a
 * reload must not wipe what the user is typing.
 */
export function useWorkflowDraft(workflows: IssueWorkflowOutDto[], workflowId: string | undefined): WorkflowDraft {
  const [workflowName, setWorkflowName] = useState('');
  const [localStages, setLocalStages] = useState<WorkflowStageRow[]>([]);
  const originalSnapshotRef = useRef<WorkflowOriginalSnapshot | null>(null);
  const draftWorkflowIdRef = useRef<string | undefined>(undefined);

  const workflow = useMemo(
    () => (workflowId ? findById(workflows, workflowId) ?? null : null),
    [workflows, workflowId]
  );
  const serverSignature = useMemo(() => buildWorkflowContentSignature(workflow), [workflow]);

  const workflowsRef = useLatestRef(workflows);
  const draftRef = useLatestRef({ workflowName, localStages });

  const resetToServer = useCallback((): void => {
    const serverWorkflow = workflowId ? findById(workflowsRef.current, workflowId) : undefined;
    draftWorkflowIdRef.current = workflowId;
    if (!serverWorkflow) {
      originalSnapshotRef.current = null;
      setWorkflowName('');
      setLocalStages([]);
      return;
    }
    const stages = findStagesByWorkflowId(workflowsRef.current, serverWorkflow._id);
    originalSnapshotRef.current = buildOriginalSnapshot(serverWorkflow.name, stages);
    setWorkflowName(serverWorkflow.name);
    setLocalStages(stages.map(serverStageToRow));
  }, [workflowId, workflowsRef]);

  const isDraftChanged = useCallback((): boolean => {
    const original = originalSnapshotRef.current;
    if (!original) {
      return false;
    }
    const draft = draftRef.current;
    return (
      draft.workflowName !== original.name ||
      hasWorkflowChanges(computeSaveDiff(original, draft.workflowName, draft.localStages))
    );
  }, [draftRef]);

  // The server's content, not each reload of the list (a new array of the same workflows).
  useEffect(() => {
    const isAnotherWorkflow = draftWorkflowIdRef.current !== workflowId || !originalSnapshotRef.current;
    if (isAnotherWorkflow || !isDraftChanged()) {
      resetToServer();
    }
  }, [workflowId, serverSignature, isDraftChanged, resetToServer]);

  const getOriginalSnapshot = useCallback((): WorkflowOriginalSnapshot | null => originalSnapshotRef.current, []);

  return {
    workflow,
    workflowName,
    setWorkflowName,
    localStages,
    setLocalStages,
    getOriginalSnapshot,
    resetToServer,
  };
}
